'use client';

import Link from 'next/link';
import { FormEvent, useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';

type LeadPayload = {
  fullName: string;
  email: string;
  phone: string | null;
  address?: string | null;
  preferred: string;
  bestTime: string;
  notes: string;
  brand: string;
  brandLabel: string;
  metaEventName: 'Lead';
};

const SERVICE_COPY: Record<string, string> = {
  roofing: 'Roofing',
  water: 'Water Softeners',
  lighting: 'Permanent Lighting',
  generators: 'Back Up Power (Generators)',
  solar: 'Back Up Power (Generators)',
};

const REQUESTED_SERVICES = [
  'Batteries',
  'Electrical Upgrades',
  'Generators',
  'Permanent Home Lighting',
  'Asphalt Roofing',
  'Solar Panels',
  'Solar Repair',
  'Water Softener',
  'Other',
] as const;

const DEFAULT_SERVICE_BY_PAGE: Record<string, string> = {
  roofing: 'Asphalt Roofing',
  water: 'Water Softener',
  lighting: 'Permanent Home Lighting',
  generators: 'Generators',
  solar: 'Solar Panels',
};

export default function LeadFormPageClient() {
  const params = useSearchParams();
  const serviceKey = (params.get('service') || '').toLowerCase();
  const serviceLabel = SERVICE_COPY[serviceKey] || 'Home Upgrade';

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [zip, setZip] = useState('');
  const [bestTime, setBestTime] = useState('');
  const [selectedServices, setSelectedServices] = useState<string[]>(() => {
    const defaultService = DEFAULT_SERVICE_BY_PAGE[serviceKey];
    return defaultService ? [defaultService] : [];
  });
  const [otherServiceText, setOtherServiceText] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const title = useMemo(() => `Get your ${serviceLabel} plan`, [serviceLabel]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSuccess(false);

    if (!fullName.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!phone.trim()) {
      setError('Please enter your phone number.');
      return;
    }
    if (!bestTime.trim()) {
      setError('Please tell us the best time to reach you.');
      return;
    }
    if (!selectedServices.length) {
      setError('Please select at least one service.');
      return;
    }

    const serviceList = selectedServices.includes('Other') && otherServiceText.trim()
      ? [...selectedServices.filter((s) => s !== 'Other'), `Other: ${otherServiceText.trim()}`]
      : selectedServices;

    const payload: LeadPayload = {
      fullName: fullName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      preferred: 'Phone',
      bestTime: bestTime.trim(),
      notes: `Primary page: ${serviceLabel} | Services Requested: ${serviceList.join(', ')}${zip.trim() ? ` | ZIP: ${zip.trim()}` : ''}${notes.trim() ? ` | Notes: ${notes.trim()}` : ''}`,
      brand: 'home_pros',
      brandLabel: 'Home Pros for Texas',
      metaEventName: 'Lead',
    };

    try {
      setIsSubmitting(true);
      const response = await fetch('/api/send-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Unable to submit right now.');
      }

      setSuccess(true);
      setFullName('');
      setEmail('');
      setPhone('');
      setZip('');
      setBestTime('');
      setSelectedServices([]);
      setOtherServiceText('');
      setNotes('');
    } catch (err: any) {
      setError(err?.message || 'Unable to submit right now.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#f7f8f4] px-4 py-10 sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute -left-16 top-0 h-72 w-72 rounded-full bg-emerald-300/30 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 bottom-0 h-80 w-80 rounded-full bg-amber-300/30 blur-3xl" />

      <div className="relative mx-auto max-w-3xl">
        <Link
          href="/home-pros"
          className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-600 hover:text-slate-900"
        >
          Back to Home Pros
        </Link>

        <section className="mt-4 rounded-[28px] border border-white/70 bg-white/90 p-6 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.25)] backdrop-blur sm:p-8">
          <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-emerald-700">Request Consultation</p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">{title}</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
            Two-minute form. A Home Pros advisor calls you with next steps and scheduling options.
          </p>

          <form className="mt-7 grid gap-4 sm:grid-cols-2" onSubmit={onSubmit} noValidate>
            {error ? (
              <p className="sm:col-span-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>
            ) : null}
            {success ? (
              <p className="sm:col-span-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                Thanks, we got it. A Home Pros advisor will reach out shortly.
              </p>
            ) : null}

            <label className="sm:col-span-2">
              <span className="mb-1 block text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Full name</span>
              <input
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-400"
                placeholder="John Doe"
              />
            </label>

            <label>
              <span className="mb-1 block text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Phone</span>
              <input
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-400"
                placeholder="(210) 555-1234"
              />
            </label>

            <label>
              <span className="mb-1 block text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Email (optional)</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-400"
                placeholder="you@email.com"
              />
            </label>

            <label>
              <span className="mb-1 block text-xs font-bold uppercase tracking-[0.18em] text-slate-500">ZIP code</span>
              <input
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-400"
                placeholder="78259"
              />
            </label>

            <label>
              <span className="mb-1 block text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Best time to meet/call</span>
              <input
                required
                value={bestTime}
                onChange={(e) => setBestTime(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-400"
                placeholder="Weekdays after 5 PM"
              />
            </label>

            <div className="sm:col-span-2 rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Services requested</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {REQUESTED_SERVICES.map((service) => {
                  const checked = selectedServices.includes(service);
                  return (
                    <label key={service} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedServices((prev) => [...prev, service]);
                          } else {
                            setSelectedServices((prev) => prev.filter((s) => s !== service));
                          }
                        }}
                      />
                      <span>{service}</span>
                    </label>
                  );
                })}
              </div>
              {selectedServices.includes('Other') ? (
                <input
                  value={otherServiceText}
                  onChange={(e) => setOtherServiceText(e.target.value)}
                  className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-400"
                  placeholder="Other service needed"
                />
              ) : null}
            </div>

            <label className="sm:col-span-2">
              <span className="mb-1 block text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Project notes (optional)</span>
              <textarea
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-400"
                placeholder="Timeline, issue, or anything we should know."
              />
            </label>

            <button
              type="submit"
              disabled={isSubmitting}
              className="sm:col-span-2 rounded-2xl bg-gradient-to-r from-emerald-600 to-emerald-500 px-6 py-4 text-sm font-black uppercase tracking-[0.2em] text-white shadow-lg transition hover:-translate-y-0.5 disabled:opacity-60"
            >
              {isSubmitting ? 'Sending...' : `Request ${serviceLabel} Consult`}
            </button>
          </form>
        </section>
      </div>
    </main>
  );
}
